// src/pages/Profile.jsx
import React, { useState, useEffect } from 'react';
import { authAPI, ordersAPI, chatAPI, productsAPI } from '../services/api';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  ShoppingBag, 
  Package,
  Clock,
  Truck,
  CheckCircle,
  Coins,
  Tag,
  Calendar,
  Loader,
  AlertCircle,
  Save,
  Eye,
  BarChart3,
  MessageCircle,
  Send,
  XCircle,
  Settings
} from 'lucide-react';
import './css/Profile.css';

const Profile = () => {
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    shippingAddress: ''
  });
  const [orders, setOrders] = useState([]);
  const [enrichedOrders, setEnrichedOrders] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const formatOptionLabel = (group, value) => {
    const extraPrice = Number(value.extra_price) || 0;
    const extra = extraPrice > 0 ? ` (+DT ${extraPrice.toFixed(2)})` : '';
    return `${group.name}: ${value.value}${extra}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profileRes, ordersRes, convosRes] = await Promise.all([
          authAPI.getProfile(),
          ordersAPI.getMyOrders(),
          chatAPI.getUserConversations().catch(() => ({ data: [] }))
        ]);

        setProfile(profileRes.data);
        setOrders(ordersRes.data);
        setConversations(convosRes.data || []);

        if (ordersRes.data.length > 0) {
          const itemsWithOptions = ordersRes.data.flatMap(order =>
            order.items.filter(item => {
              try {
                const opts = typeof item.selected_options === 'string' ? JSON.parse(item.selected_options) : item.selected_options;
                return opts && Object.keys(opts).length > 0;
              } catch {
                return false;
              }
            })
          );

          let enriched = [...ordersRes.data];

          if (itemsWithOptions.length > 0) {
            const uniqueProductIds = [...new Set(itemsWithOptions.map(item => item.productId))];
            const productResponses = await Promise.allSettled(
              uniqueProductIds.map(pid => productsAPI.getById(pid))
            );

            const productsMap = {};
            productResponses.forEach((resp, idx) => {
              if (resp.status === 'fulfilled') {
                productsMap[uniqueProductIds[idx]] = resp.value.data;
              }
            });

            enriched = ordersRes.data.map(order => {
              const enrichedItems = order.items.map(item => {
                let parsedOptions = {};
                try {
                  parsedOptions = typeof item.selected_options === 'string' ? JSON.parse(item.selected_options) : item.selected_options;
                } catch {
                  parsedOptions = {};
                }

                const fullProduct = productsMap[item.productId];
                if (!fullProduct || Object.keys(parsedOptions).length === 0) {
                  return {
                    ...item,
                    finalPrice: Number(item.price || 0),
                    optionLabels: null
                  };
                }

                const basePrice = Number(fullProduct.salePrice || fullProduct.regularPrice || 0);
                let optionExtra = 0;
                const labels = [];

                fullProduct.options.forEach(group => {
                  const selectedValueId = parsedOptions[group.id];
                  if (selectedValueId) {
                    const value = group.values.find(v => v.id === Number(selectedValueId));
                    if (value) {
                      const extra = Number(value.extra_price) || 0;
                      optionExtra += extra;
                      labels.push(formatOptionLabel(group, value));
                    }
                  }
                });

                const finalPrice = basePrice + optionExtra;

                return {
                  ...item,
                  finalPrice,
                  optionLabels: labels.length > 0 ? labels : null,
                  name: fullProduct.name || item.name,
                  selected_options: parsedOptions
                };
              });

              return { ...order, items: enrichedItems };
            });
          } else {
            enriched = enriched.map(order => ({
              ...order,
              items: order.items.map(item => ({
                ...item,
                finalPrice: Number(item.price || 0),
                optionLabels: null
              }))
            }));
          }

          setEnrichedOrders(enriched);
        }
      } catch (err) {
        setError('Impossible de charger le profil');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (conversations.length === 0) return;

    const interval = setInterval(async () => {
      try {
        const updated = await Promise.all(
          conversations.map(async (conv) => {
            const statusRes = await chatAPI.getConversationStatus(conv.id);
            return { ...conv, ...statusRes.data };
          })
        );
        setConversations(updated);
      } catch (err) {
        console.warn('Failed to update convo status');
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [conversations]);

  const handleInputChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await authAPI.updateProfile(profile);
      setSuccess('Profil mis a jour avec succes !');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Echec de mise a jour du profil');
      setTimeout(() => setError(''), 3000);
    }
  };

  const computeOrderTotal = (order) => {
    const subtotal = order.items.reduce((sum, item) => sum + (item.finalPrice || Number(item.price || 0)) * (item.quantity || 0), 0);
    return Math.max(0, subtotal - Number(order.discount_amount || 0) + Number(order.delivery_fee || 0));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock size={14} />;
      case 'processing': return <Package size={14} />;
      case 'shipped': return <Truck size={14} />;
      case 'delivered': return <CheckCircle size={14} />;
      default: return <ShoppingBag size={14} />;
    }
  };

  const getOrderStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'En attente';
      case 'processing': return 'En cours';
      case 'shipped': return 'Expediee';
      case 'delivered': return 'Livree';
      default: return status;
    }
  };

  const getConversationStatusLabel = (status) => {
    if (status === 'open') return 'ouverte';
    if (status === 'closed') return 'fermee';
    return status;
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return '/placeholder-image.jpg';
    return imageUrl.startsWith('http') ? imageUrl : `http://localhost:5000/images/${imageUrl}`;
  };

  const openChat = (convoId) => {
    window.open(`/chat?convo=${convoId}`, '_blank', 'width=800,height=700');
  };

  const formatConvoDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const orderStats = {
    total: enrichedOrders.length,
    totalSpent: enrichedOrders.reduce((sum, order) => sum + computeOrderTotal(order), 0),
    pending: enrichedOrders.filter(o => o.status === 'pending').length,
    processing: enrichedOrders.filter(o => o.status === 'processing').length,
    shipped: enrichedOrders.filter(o => o.status === 'shipped').length,
    delivered: enrichedOrders.filter(o => o.status === 'delivered').length,
  };

  if (loading) {
    return (
      <div className="profile-loading-container">
        <Loader className="profile-loading-spinner" size={40} />
        <p className="profile-loading-text">Chargement de votre profil...</p>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <div className="profile-header-content">
          <div className="profile-header-icon">
            <User size={28} />
          </div>
          <div>
            <h1 className="profile-header-title">Mon profil</h1>
            <p className="profile-header-subtitle">Gerez vos informations, commandes et conversations d'assistance</p>
          </div>
        </div>
      </div>

      {(error || success) && (
        <div className="profile-alerts">
          {error && (
            <div className="profile-alert profile-alert-error">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="profile-alert profile-alert-success">
              <CheckCircle size={20} />
              <span>{success}</span>
            </div>
          )}
        </div>
      )}

      {enrichedOrders.length > 0 && (
        <div className="profile-stats">
          <div className="profile-stat-card">
            <div className="profile-stat-icon profile-stat-total">
              <ShoppingBag size={20} />
            </div>
            <div>
              <div className="profile-stat-label">Total des commandes</div>
              <div className="profile-stat-value">{orderStats.total}</div>
            </div>
          </div>
          <div className="profile-stat-card">
            <div className="profile-stat-icon profile-stat-spent">
              <Coins size={20} />
            </div>
            <div>
              <div className="profile-stat-label">Total depense</div>
              <div className="profile-stat-value">DT {orderStats.totalSpent.toFixed(2)}</div>
            </div>
          </div>
          <div className="profile-stat-card">
            <div className="profile-stat-icon profile-stat-pending">
              <Clock size={20} />
            </div>
            <div>
              <div className="profile-stat-label">En attente</div>
              <div className="profile-stat-value">{orderStats.pending}</div>
            </div>
          </div>
          <div className="profile-stat-card">
            <div className="profile-stat-icon profile-stat-delivered">
              <CheckCircle size={20} />
            </div>
            <div>
              <div className="profile-stat-label">Livrees</div>
              <div className="profile-stat-value">{orderStats.delivered}</div>
            </div>
          </div>
        </div>
      )}

      <div className="profile-content">
        <div className="profile-grid">
          <div>
            <div className="profile-card">
              <div className="profile-card-header">
                <User size={20} />
                <h2 className="profile-card-title">Informations personnelles</h2>
              </div>
              <form onSubmit={handleSubmit} className="profile-form">
                <div className="form-group">
                  <label className="form-label">
                    <User size={16} />
                    Prenom
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={profile.firstName}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Entrez votre prenom"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <User size={16} />
                    Nom
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={profile.lastName}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Entrez votre nom"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <Mail size={16} />
                    Adresse email
                  </label>
                  <input
                    type="email"
                    value={profile.email}
                    className="form-input form-input-disabled"
                    disabled
                  />
                  <span className="form-hint">Email non modifiable</span>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <Phone size={16} />
                    Numero de telephone
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={profile.phoneNumber}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Entrez votre numero de telephone"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <MapPin size={16} />
                    Adresse de livraison
                  </label>
                  <textarea
                    name="shippingAddress"
                    value={profile.shippingAddress}
                    onChange={handleInputChange}
                    className="form-textarea"
                    placeholder="Entrez votre adresse de livraison"
                    rows={4}
                  />
                </div>

                <button type="submit" className="btn-primary">
                  <Save size={18} />
                  Mettre a jour le profil
                </button>
              </form>
            </div>

            <div className="profile-card profile-convos-card">
              <div className="profile-card-header">
                <MessageCircle size={20} />
                <h2 className="profile-card-title">Conversations recentes</h2>
                <span className="profile-count-badge">{conversations.length}</span>
              </div>

              {conversations.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">
                    <MessageCircle size={48} />
                  </div>
                  <h3 className="empty-title">Aucune conversation pour le moment</h3>
                  <p className="empty-text">Demarrez un chat depuis le bouton d'assistance</p>
                </div>
              ) : (
                <div className="convo-list">
                  {conversations.map((conv) => (
                    <div
                      key={conv.id}
                      className="convo-item"
                      onClick={() => openChat(conv.id)}
                    >
                      <div className="convo-header">
                        <div className="convo-subject">
                          <MessageCircle size={16} />
                          <span>{conv.subject || 'Assistance generale'}</span>
                        </div>
                        <div className="convo-meta">
                          {conv.unreadCount > 0 && (
                            <span className="convo-unread">{conv.unreadCount}</span>
                          )}
                          <span className={`convo-status convo-status-${conv.status}`}>
                            {conv.status === 'open' ? <Send size={12} /> : <XCircle size={12} />}
                            {getConversationStatusLabel(conv.status)}
                          </span>
                        </div>
                      </div>
                      <div className="convo-date">
                        <Calendar size={12} />
                        {formatConvoDate(conv.updated_at || conv.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="profile-card profile-orders-card">
            <div className="profile-card-header">
              <Package size={20} />
              <h2 className="profile-card-title">Historique des commandes</h2>
              <span className="profile-count-badge">{enrichedOrders.length}</span>
            </div>
            
            {enrichedOrders.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">
                  <ShoppingBag size={48} />
                </div>
                <h3 className="empty-title">Aucune commande pour le moment</h3>
                <p className="empty-text">Votre historique apparaitra apres votre premier achat</p>
              </div>
            ) : (
              <div className="orders-list">
                {enrichedOrders.map((order) => {
                  const orderItems = order.items || [];
                  const subtotal = orderItems.reduce((sum, item) => sum + (item.finalPrice || Number(item.price || 0)) * (item.quantity || 0), 0);
                  const discount = Number(order.discount_amount || 0);
                  const deliveryFee = Number(order.delivery_fee || 0);
                  const total = computeOrderTotal(order);
                  
                  return (
                    <div key={order.id} className="order-card">
                      <div className="order-header">
                        <div className="order-info">
                          <div className="order-id">
                            <Package size={16} />
                            <span>Commande #{order.id}</span>
                          </div>
                          <div className="order-date">
                            <Calendar size={14} />
                          <span>{new Date(order.createdAt).toLocaleDateString('fr-FR', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}</span>
                          </div>
                        </div>
                        <span className={`order-status status-${order.status}`}>
                          {getStatusIcon(order.status)}
                          <span>{getOrderStatusLabel(order.status)}</span>
                        </span>
                      </div>

                      {orderItems.length > 0 && (
                        <div className="order-items">
                          <div className="items-header">
                            <ShoppingBag size={16} />
                            <span>Articles de la commande ({orderItems.length})</span>
                          </div>
                          {orderItems.map((item, idx) => (
                            <div key={idx} className="order-item">
                              <div className="item-image">
                                <img
                                  src={getImageUrl(item.imageUrl)}
                                  alt={item.name || 'Product'}
                                  loading="lazy"
                                  decoding="async"
                                  onError={(e) => { e.target.src = '/placeholder-image.jpg'; }}
                                />
                              </div>
                              <div className="item-details">
                                <h4 className="item-name">{item.name || 'Product'}</h4>
                                {item.optionLabels && item.optionLabels.length > 0 && (
                                  <div className="item-options">
                                    <Settings size={12} />
                                    <span>{item.optionLabels.join(', ')}</span>
                                  </div>
                                )}
                                <div className="item-meta">
                                  <span className="item-price">
                                    <Coins size={12} />
                                    DT {item.finalPrice ? item.finalPrice.toFixed(2) : Number(item.price || 0).toFixed(2)} each
                                  </span>
                                  <span className="item-qty">Qté : {item.quantity || 0}</span>
                                </div>
                              </div>
                              <div className="item-total">
                                <Coins size={14} />
                                DT {((item.finalPrice || Number(item.price || 0)) * (item.quantity || 0)).toFixed(2)}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="order-details">
                        {order.shippingAddress && (
                          <div className="detail-row">
                            <MapPin size={14} />
                            <div>
                              <span className="detail-label">Adresse de livraison</span>
                              <span className="detail-value">{order.shippingAddress}</span>
                            </div>
                          </div>
                        )}
                        {order.phoneNumber && (
                          <div className="detail-row">
                            <Phone size={14} />
                            <div>
                              <span className="detail-label">Numero de contact</span>
                              <span className="detail-value">{order.phoneNumber}</span>
                            </div>
                          </div>
                        )}
                        {order.trackingCode && (
                          <div className="detail-row">
                            <Truck size={14} />
                            <div>
                              <span className="detail-label">Code de suivi</span>
                              <span className="tracking-code">{order.trackingCode}</span>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="order-summary">
                        <div className="summary-row">
                          <span>Sous-total</span>
                          <span>DT {subtotal.toFixed(2)}</span>
                        </div>
                        {discount > 0 && (
                          <div className="summary-row discount">
                            <span>
                              <Tag size={12} />
                              Remise {order.promo_code && `(${order.promo_code})`}
                            </span>
                            <span>-DT {discount.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="summary-row">
                          <span>
                            <Truck size={12} />
                            Frais de livraison
                          </span>
                          <span className="delivery-free">
                            {deliveryFee === 0 ? 'Gratuite' : `DT ${deliveryFee.toFixed(2)}`}
                          </span>
                        </div>
                        <div className="summary-divider"></div>
                        <div className="summary-total">
                          <span>Montant total</span>
                          <span className="total-value">
                            <Coins size={16} />
                            DT {total.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
